import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ANALYTICAL_DATASET_WRITE_MUTATION_KEY } from '@verantos-dev/core-constants';
import { authFetch } from '@verantos-dev/core-utils';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { analyticalDatasetKeys } from './queryKeys';
import type {
  AnalyticalDatasetCohortEntry,
  AnalyticalDatasetVariableEntry,
  AnalyticalDatasetVersion,
} from './types';

interface SaveAnalyticalDatasetInput {
  projectId: string;
  analyticalDatasetProfileId: string;
  /** The currently-loaded version id — PATCH target. Backend appends a new
   *  version row and re-points the profile pointer at it. */
  analyticalDatasetVersionId: string;
  cohorts: AnalyticalDatasetCohortEntry[];
  variables: AnalyticalDatasetVariableEntry[];
}

export const useSaveAnalyticalDatasetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Tag so useProjectPageAnalyticalDatasetSync can bridge this write to the
    // project artifact manifest + activity feed via the mutation cache.
    mutationKey: [ANALYTICAL_DATASET_WRITE_MUTATION_KEY],
    mutationFn: ({
      projectId,
      analyticalDatasetProfileId,
      analyticalDatasetVersionId,
      cohorts,
      variables,
    }: SaveAnalyticalDatasetInput) =>
      authFetch<ObjectResponse<AnalyticalDatasetVersion>>(
        `/analytical-datasets/${encodeURIComponent(projectId)}` +
          `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}` +
          `/analytical-dataset-version/${encodeURIComponent(analyticalDatasetVersionId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ jsonValue: { cohorts, variables } }),
        }
      ),
    onSuccess: (_data, variables) => {
      // Profile's `latestAnalyticalDatasetVersionId` changed; refetch so the
      // resolver / breadcrumb / meta row pick up the new id. List query keeps
      // its `modifiedBy.transactionDate` column fresh.
      queryClient.invalidateQueries({
        queryKey: analyticalDatasetKeys.profile(variables.analyticalDatasetProfileId),
      });
      queryClient.invalidateQueries({
        queryKey: analyticalDatasetKeys.list(variables.projectId),
      });
    },
  });
};
